import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import moment from 'moment';

import { isTravelAgent } from 'utils/user';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_ON_HOLD_BOOKINGS } from 'store/queries';
import { ACTION_CODES } from 'constants/errors';
import { historyShape, userShape } from 'constants/propTypes';
import BookingSearchTable from './BookingSearchTable';
import BookingSearchSummary from './BookingSearchSummary';
import SearchByOnHoldPaymentMonth from '../BookingSearch/SearchByOnHoldPaymentMonth';

import { BookingSearchOnHoldResultsHeader } from './BookingOnHoldSearchResults.styles';

const dateFormat = 'ddd DD MMM YYYY';

const BookingSearchOnHoldResults = ({ history, user }) => {
  const {
    location: { search }
  } = history;

  const getSearchParam = (param) => new URLSearchParams(search).get(param);
  const expiry = getSearchParam('expiry');
  const officeLocation = getSearchParam('officeLocation');
  const queryFilteredOfficeLocation = getSearchParam('filteredOfficeLocation');

  // used in query to filter bookings by search term entered
  const filteredOfficeLocation = queryFilteredOfficeLocation ? queryFilteredOfficeLocation.toUpperCase() : null;

  const { data, error, loading } = useQuery(GET_ON_HOLD_BOOKINGS, {
    variables: officeLocation ? { expiry, officeLocation, filteredOfficeLocation } : { expiry },
    fetchPolicy: 'network-only'
  });

  if (loading) {
    return <Spinner data-testid="booking-search-on-hold-results-loading" />;
  }

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/search/booking'
    });
  }

  const { onHoldBookings } = data;

  const bookings = onHoldBookings.map(
    ({ customer, externalBookingReferences = [], expiryTimestamp, inbound, outbound, passengers, reference, status, agent }) => {
      const passengerCount = passengers.length;

      const firstName = customer?.firstName;
      const lastName = customer?.lastName;

      const departingTo = outbound[0]?.destination?.shortName;
      const departingFrom = outbound[0]?.origin?.shortName;
      const departingOnRaw = outbound[0]?.timing?.isoDate;
      const returningOnRaw = inbound[0]?.timing?.isoDate;

      const paymentDue = expiryTimestamp ? moment(expiryTimestamp).format(dateFormat) : undefined;
      const departingOn = departingOnRaw ? moment(departingOnRaw).format(dateFormat) : undefined;
      const returningOn = returningOnRaw ? moment(returningOnRaw).format(dateFormat) : undefined;

      const agentOffice = agent?.office;

      const masterReference = externalBookingReferences.reduce(
        (current, { source, externalId }) => (source === 'MASTER' ? externalId : current),
        undefined
      );

      return {
        reference,
        masterReference,
        status,
        agentOffice,
        paymentDue,
        firstName,
        lastName,
        passengerCount,
        departingFrom,
        departingOn,
        departingTo,
        returningOn
      };
    }
  );

  return (
    <>
      <BookingSearchOnHoldResultsHeader>
        <div>
          <BookingSearchSummary data-testid="booking-search-summary" noOfPnrsFound={bookings.length} onHoldExpiry={expiry} />
        </div>
        <SearchByOnHoldPaymentMonth
          data-testid="search-by-on-hold-payment-month"
          defaultMonth={expiry}
          defaultOfficeLocation={officeLocation}
          isNonEurostarUser={isTravelAgent(user)}
          onSubmitSearch={({ searchPaymentMonth, searchOfficeLocation, filteredOfficeLocation }) => {
            history.push(
              `/bookings?expiry=${searchPaymentMonth}${
                !isTravelAgent(user)
                  ? `&officeLocation=${searchOfficeLocation}${
                      filteredOfficeLocation ? `&filteredOfficeLocation=${filteredOfficeLocation}` : ``
                    }`
                  : ''
              }`
            );
          }}
          filteredOfficeLocation={filteredOfficeLocation}
        />
      </BookingSearchOnHoldResultsHeader>
      {Boolean(bookings.length) && (
        <BookingSearchTable
          data-testid="booking-search-table"
          bookings={bookings}
          defaultSortKey="paymentDue"
          removeColumns={['minorsAmount', 'specialAssistance', 'firstName']}
        />
      )}
    </>
  );
};

BookingSearchOnHoldResults.propTypes = {
  history: historyShape,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  }),
  user: userShape
};

export default BookingSearchOnHoldResults;
